import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { mediaUrl } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

const CATEGORIES = [
  { slug: "portraits", label: "Portraits" },
  { slug: "weddings", label: "Weddings" },
  { slug: "events", label: "Events" },
  { slug: "matric-balls", label: "Matric Balls" },
  { slug: "graduations", label: "Graduations" },
  { slug: "sports", label: "Sports" },
  { slug: "films", label: "Films only" },
];

export default function Booking() {
  const [params] = useSearchParams();
  const { services, settings } = useSite();
  const initialCategory = params.get("category") || "portraits";
  const initialPackage = params.get("package") || "";

  const [pkgs, setPkgs] = useState([]);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    mobile: "",
    preferred_contact: "email",
    service_category: initialCategory,
    selected_package: initialPackage,
    preferred_date: "",
    alternative_date: "",
    start_time: "",
    venue: "",
    guest_count: "",
    photography_or_film: "Photography only",
    heard_from: "",
    additional_info: "",
    consent: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    if (!form.service_category || form.service_category === "films") {
      setPkgs([]);
      return;
    }
    api
      .get("/packages", { params: { category: form.service_category } })
      .then(({ data }) => setPkgs(data || []))
      .catch(() => setPkgs([]));
  }, [form.service_category]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.mobile) {
      toast.error("Please complete name, email and mobile.");
      return;
    }
    if (!form.consent) {
      toast.error("Please accept the privacy policy.");
      return;
    }
    try {
      setSubmitting(true);
      const { data } = await api.post("/enquiries", form);
      setConfirmation(data);
    } catch {
      toast.error("Enquiry failed. Please try again or WhatsApp us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const hero = services?.find((s) => s.slug === form.service_category);

  if (confirmation) {
    return (
      <main className="bg-ivory text-ink pt-32 md:pt-40 pb-32 min-h-screen" data-testid="booking-confirmation">
        <section className="px-6 md:px-12 lg:px-16 max-w-4xl">
          <p className="label-mono opacity-60 line-badge">Enquiry received</p>
          <h1 className="mt-8 font-editorial text-6xl md:text-8xl leading-[0.95]">
            <em className="italic">Thank you.</em>
          </h1>
          <p className="mt-10 font-editorial text-3xl md:text-4xl italic leading-[1.2]">
            {confirmation.message}
          </p>
          <div className="mt-10 border-t border-ink/15 pt-6 flex flex-col md:flex-row gap-6 items-start md:items-center justify-between">
            <div>
              <p className="label-mono opacity-60">Your reference</p>
              <p className="font-editorial text-3xl mt-2" data-testid="booking-reference">
                {confirmation.reference}
              </p>
            </div>
            <div className="flex gap-4 flex-wrap">
              <Link to="/portfolio" className="btn-outline-ink" data-testid="booking-confirm-portfolio">
                See the work
              </Link>
              <Link to="/" className="btn-outline-ink" data-testid="booking-confirm-home">
                Home
              </Link>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main data-testid="booking-page" className="bg-ivory text-ink pt-32 md:pt-40 pb-32">
      <div className="grid grid-cols-12 gap-8">
        {/* left visual */}
        <div className="col-span-12 md:col-span-5 px-6 md:pl-16 md:pr-0">
          <p className="label-mono opacity-60 line-badge">Booking Enquiry</p>
          <h1 className="mt-8 font-editorial text-6xl md:text-7xl leading-[0.95]">
            Begin your <em className="italic">story</em>.
          </h1>
          <p className="mt-6 text-lg text-ink/70 max-w-md">
            Send the details below. Elvis will respond with availability and next steps.
          </p>
          {hero && (
            <motion.div
              className="mt-12 photo-frame aspect-[4/5]"
              key={hero.slug}
              initial={{ opacity: 0, scale: 1.03 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
            >
              <img src={mediaUrl(hero.hero_image || hero.tile_image)} alt={hero.name} />
            </motion.div>
          )}
          <div className="mt-8 text-sm text-ink/60 leading-relaxed">
            The correct booking process is: enquiry → availability confirmation → quotation & agreement → retainer payment → confirmed booking. Dates are only secured after retainer.
          </div>
        </div>

        <form onSubmit={submit} className="col-span-12 md:col-span-7 px-6 md:pr-16 md:pl-12 space-y-6" data-testid="booking-form">
          <div>
            <label className="label-mono opacity-60">Full name</label>
            <input required className="editorial-input" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} data-testid="booking-input-name" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label-mono opacity-60">Email</label>
              <input required type="email" className="editorial-input" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="booking-input-email" />
            </div>
            <div>
              <label className="label-mono opacity-60">Mobile</label>
              <input required className="editorial-input" value={form.mobile} onChange={(e) => update("mobile", e.target.value)} data-testid="booking-input-mobile" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label-mono opacity-60">Preferred contact</label>
              <select className="editorial-select" value={form.preferred_contact} onChange={(e) => update("preferred_contact", e.target.value)} data-testid="booking-input-preferred-contact">
                <option value="email">Email</option>
                <option value="phone">Phone call</option>
                <option value="whatsapp">WhatsApp</option>
              </select>
            </div>
            <div>
              <label className="label-mono opacity-60">Photography category</label>
              <select className="editorial-select" value={form.service_category} onChange={(e) => update("service_category", e.target.value)} data-testid="booking-input-category">
                {CATEGORIES.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>
          {pkgs.length > 0 && (
            <div>
              <label className="label-mono opacity-60">Selected package</label>
              <select className="editorial-select" value={form.selected_package} onChange={(e) => update("selected_package", e.target.value)} data-testid="booking-input-package">
                <option value="">Not sure yet</option>
                {pkgs.map((p) => (
                  <option key={p.id} value={p.name}>{p.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="label-mono opacity-60">Preferred date</label>
              <input type="date" className="editorial-input" value={form.preferred_date} onChange={(e) => update("preferred_date", e.target.value)} data-testid="booking-input-date" />
            </div>
            <div>
              <label className="label-mono opacity-60">Alternative date</label>
              <input type="date" className="editorial-input" value={form.alternative_date} onChange={(e) => update("alternative_date", e.target.value)} data-testid="booking-input-alt-date" />
            </div>
            <div>
              <label className="label-mono opacity-60">Start time</label>
              <input type="time" className="editorial-input" value={form.start_time} onChange={(e) => update("start_time", e.target.value)} data-testid="booking-input-time" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label-mono opacity-60">Venue or location</label>
              <input className="editorial-input" value={form.venue} onChange={(e) => update("venue", e.target.value)} data-testid="booking-input-venue" />
            </div>
            <div>
              <label className="label-mono opacity-60">Estimated guests / participants</label>
              <input className="editorial-input" value={form.guest_count} onChange={(e) => update("guest_count", e.target.value)} data-testid="booking-input-guests" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label-mono opacity-60">Coverage</label>
              <select className="editorial-select" value={form.photography_or_film} onChange={(e) => update("photography_or_film", e.target.value)} data-testid="booking-input-coverage">
                <option>Photography only</option>
                <option>Photography and film</option>
                <option>Film only</option>
              </select>
            </div>
            <div>
              <label className="label-mono opacity-60">How did you hear about us?</label>
              <input className="editorial-input" value={form.heard_from} onChange={(e) => update("heard_from", e.target.value)} data-testid="booking-input-heard" />
            </div>
          </div>
          <div>
            <label className="label-mono opacity-60">Additional information</label>
            <textarea rows={5} className="editorial-textarea" value={form.additional_info} onChange={(e) => update("additional_info", e.target.value)} data-testid="booking-input-info" />
          </div>
          <label className="flex items-start gap-3 text-sm text-ink/70">
            <input type="checkbox" className="mt-1" checked={form.consent} onChange={(e) => update("consent", e.target.checked)} data-testid="booking-input-consent" />
            I agree to the <Link to="/privacy" className="underline">privacy policy</Link>. My details will be used only for this enquiry.
          </label>
          <div className="pt-4">
            <button type="submit" disabled={submitting} className="btn-solid" data-testid="booking-submit">
              {submitting ? "Sending…" : "Submit enquiry"}
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}
